import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import SectionLoading from '@components/SectionLoading';
import Text, { TEXT_SIZE, TEXT_COLOR } from '@components/Text';
import SortOrder from '@components/SortableTable/components/SortOrder';
import { PLACEMENT } from '@components/CustomTextInput';
import { getIconsByType, iconTypes } from '@services/icons';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import { ORDERS } from '@shared/constants';
import {
  createBusinessUnit,
  getBusinessUnits,
  removeBusinessUnit,
  updateBusinessUnit,
} from './services';
import {
  PageShell,
  Container,
  Header,
  HeaderActions,
  SummaryBadge,
  SearchContainer,
  SearchInput,
  SearchInputIcon,
  Input,
  AddIconButton,
  SaveIconButton,
  EditIconButton,
  CancelIconButton,
  DeleteIconButton,
  TableWrapper,
  Table,
  Th,
  Td,
  Row,
  DraftRow,
  ActionsRow,
} from './styles';
import {
  getGraphErrorMessage,
  sortByName,
  hasGraphErrors,
  getBusinessUnitsLabelText,
} from './helpers';

const AddIcon = getIconsByType(iconTypes.ADD);
const SaveIcon = getIconsByType(iconTypes.SAVE);
const EditIcon = getIconsByType(iconTypes.EDIT);
const CancelIcon = getIconsByType(iconTypes.CLOSE);
const DeleteIcon = getIconsByType(iconTypes.DELETE);

// TODO: Long-term, migrate this screen to TSX + Redux/Saga to align with complex Admin modules.
// For now, we keep hooks + local state because this screen is still simple CRUD and does not require shared saga flows.
// NOTE: We intentionally keep a custom table here (instead of SortableTable) because BU rows are inline-editable
// form rows with a draft-create row. The current SortableTable/virtualized layout is optimized for display rows
// and caused unstable row rendering for this edit-heavy use case.
const BusinessUnits = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [businessUnits, setBusinessUnits] = useState([]);
  const [noBusinessUnitUsersCount, setNoBusinessUnitUsersCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState({ field: 'name', order: ORDERS.ASC });
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newBusinessUnitName, setNewBusinessUnitName] = useState('');
  const [newBusinessUnitIsTest, setNewBusinessUnitIsTest] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [editedName, setEditedName] = useState('');
  const [editedIsTest, setEditedIsTest] = useState(false);

  const businessUnitsLabel = useMemo(() => getBusinessUnitsLabelText(t), [t]);
  const filteredAndSortedBusinessUnits = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = normalizedSearch
      ? businessUnits.filter((item) => (item.name || '').toLowerCase().includes(normalizedSearch))
      : businessUnits;

    const sorted = [...filtered].sort((a, b) => {
      if (sort.field === 'usersCount') {
        return (a.usersCount || 0) - (b.usersCount || 0);
      }

      return (a.name || '').localeCompare(b.name || '');
    });

    return sort.order === ORDERS.DESC ? sorted.reverse() : sorted;
  }, [businessUnits, searchTerm, sort]);

  const isCreateDisabled = useMemo(
    () => !isCreatingNew || !newBusinessUnitName.trim() || busyId === 'create',
    [isCreatingNew, newBusinessUnitName, busyId],
  );

  const onToggleSort = useCallback((field) => {
    setSort((prev) => {
      if (prev.field === field) {
        return {
          field,
          order: prev.order === ORDERS.ASC ? ORDERS.DESC : ORDERS.ASC,
        };
      }

      return {
        field,
        order: ORDERS.ASC,
      };
    });
  }, []);

  const showErrorPopup = useCallback((message) => {
    dispatch(openPopUp(POPUP_TYPES.ALERT, {
      titleText: t('Informative.Negative.requestFailed'),
      bodyText: message,
      buttonText: t('Actions.Instance.close'),
    }));
  }, [dispatch, t]);

  const loadBusinessUnits = useCallback(async () => {
    setLoading(true);

    const result = await getBusinessUnits();
    if (hasGraphErrors(result)) {
      showErrorPopup(getGraphErrorMessage(result, t('Informative.Negative.requestFailed')));
      setLoading(false);
      return;
    }

    const allBusinessUnits = sortByName(result?.data?.allBusinessUnits || []);
    setBusinessUnits(allBusinessUnits);
    setNoBusinessUnitUsersCount(result?.data?.noBusinessUnitUsersCount || 0);
    setLoading(false);
  }, [showErrorPopup, t]);

  const onCreateBusinessUnit = useCallback(async () => {
    const normalizedName = newBusinessUnitName.trim();
    if (!normalizedName) {
      return;
    }

    setBusyId('create');
    const result = await createBusinessUnit(normalizedName, newBusinessUnitIsTest);
    if (hasGraphErrors(result)) {
      showErrorPopup(getGraphErrorMessage(result, t('Informative.Negative.requestFailed')));
      setBusyId('');
      return;
    }

    setNewBusinessUnitName('');
    setNewBusinessUnitIsTest(false);
    setIsCreatingNew(false);
    setBusyId('');
    loadBusinessUnits();
  }, [newBusinessUnitName, newBusinessUnitIsTest, loadBusinessUnits, showErrorPopup, t]);

  const onDeleteBusinessUnit = useCallback(async (businessUnitId) => {
    setBusyId(businessUnitId);
    const result = await removeBusinessUnit(businessUnitId);
    if (hasGraphErrors(result)) {
      showErrorPopup(getGraphErrorMessage(result, t('Informative.Negative.requestFailed')));
      setBusyId('');
      return;
    }

    setBusyId('');
    loadBusinessUnits();
  }, [loadBusinessUnits, showErrorPopup, t]);

  const startEditing = useCallback((businessUnit) => {
    setEditingId(businessUnit.id);
    setEditedName(businessUnit.name || '');
    setEditedIsTest(Boolean(businessUnit.isTest));
  }, []);

  const stopEditing = useCallback(() => {
    setEditingId('');
    setEditedName('');
    setEditedIsTest(false);
  }, []);

  const onUpdateBusinessUnit = useCallback(async () => {
    const normalizedName = editedName.trim();
    if (!editingId || !normalizedName) {
      return;
    }

    setBusyId(editingId);
    const result = await updateBusinessUnit(editingId, normalizedName, editedIsTest);
    if (hasGraphErrors(result)) {
      showErrorPopup(getGraphErrorMessage(result, t('Informative.Negative.requestFailed')));
      setBusyId('');
      return;
    }

    stopEditing();
    setBusyId('');
    loadBusinessUnits();
  }, [editedIsTest, editedName, editingId, loadBusinessUnits, showErrorPopup, stopEditing, t]);

  const confirmDeleteBusinessUnit = useCallback((businessUnit) => {
    dispatch(openPopUp(POPUP_TYPES.CONFIRM, {
      hideCloseIcon: true,
      titleText: t('Informative.Confirming.areYouSure'),
      bodyText: t('Informative.Negative.actionUndone'),
      confirmText: t('Actions.Confirmations.confirmDelete').toUpperCase(),
      cancelText: t('Actions.Simple.no').toUpperCase(),
      onConfirm: (confirm) => confirm && onDeleteBusinessUnit(businessUnit.id),
    }));
  }, [dispatch, onDeleteBusinessUnit, t]);

  useEffect(() => {
    loadBusinessUnits();
  }, [loadBusinessUnits]);

  if (loading) {
    return <SectionLoading />;
  }

  return (
    <PageShell>
      <Container>
        <Header>
          <Text size={TEXT_SIZE.T1} textColor={TEXT_COLOR.DEFAULT}>{businessUnitsLabel}</Text>
          <HeaderActions>
            <SummaryBadge>
              <Text size={TEXT_SIZE.T3} textColor={TEXT_COLOR.DEFAULT}>
                {`${t('Informative.Labels.usersWithNoBusinessUnit')}: ${noBusinessUnitUsersCount}`}
              </Text>
            </SummaryBadge>
            <AddIconButton
              type="button"
              aria-label={t('Actions.Instance.add')}
              title={t('Actions.Instance.add')}
              onClick={() => setIsCreatingNew(true)}
              disabled={isCreatingNew || Boolean(busyId)}
            >
              <AddIcon style={{ fontSize: 18 }} />
            </AddIconButton>
          </HeaderActions>
        </Header>

        <SearchContainer>
          <SearchInput
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('Common.Placeholders.searchOrFilter')}
            name="business-units-search"
            id="business-units-search"
            Icon={SearchInputIcon}
            iconPlacement={PLACEMENT.RIGHT}
          />
        </SearchContainer>

        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <Th onClick={() => onToggleSort('name')} clickable>
                  <span>{t('Common.UI.name')}</span>
                  {sort.field === 'name' ? <SortOrder order={sort.order} /> : null}
                </Th>
                <Th onClick={() => onToggleSort('usersCount')} clickable>
                  <span>{t('Common.Roles.users')}</span>
                  {sort.field === 'usersCount' ? <SortOrder order={sort.order} /> : null}
                </Th>
                <Th>Test</Th>
                <Th>{t('Common.UI.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {isCreatingNew && (
                <DraftRow>
                  <Td>
                    <Input
                      autoFocus
                      value={newBusinessUnitName}
                      onChange={(event) => setNewBusinessUnitName(event.target.value)}
                      placeholder="Add new Business Unit"
                      name="new-business-unit"
                    />
                  </Td>
                  <Td>0</Td>
                  <Td>
                    <Checkbox
                      checked={newBusinessUnitIsTest}
                      onChange={(event) => setNewBusinessUnitIsTest(event.target.checked)}
                      inputProps={{ 'aria-label': 'Test business unit' }}
                      disabled={busyId === 'create'}
                    />
                  </Td>
                  <Td>
                    <ActionsRow>
                      <SaveIconButton
                        type="button"
                        aria-label={t('Actions.Instance.save')}
                        title={t('Actions.Instance.save')}
                        onClick={onCreateBusinessUnit}
                        disabled={isCreateDisabled}
                      >
                        {busyId === 'create' ? <CircularProgress size={16} /> : <SaveIcon style={{ fontSize: 16 }} />}
                      </SaveIconButton>
                      <CancelIconButton
                        type="button"
                        aria-label={t('Actions.Instance.cancel')}
                        title={t('Actions.Instance.cancel')}
                        onClick={() => {
                          setIsCreatingNew(false);
                          setNewBusinessUnitName('');
                          setNewBusinessUnitIsTest(false);
                        }}
                        disabled={busyId === 'create'}
                      >
                        {busyId === 'create' ? <CircularProgress size={16} /> : <CancelIcon style={{ fontSize: 20 }} />}
                      </CancelIconButton>
                    </ActionsRow>
                  </Td>
                </DraftRow>
              )}
              {filteredAndSortedBusinessUnits.map((businessUnit) => {
                const currentBusy = busyId === businessUnit.id;
                const isEditing = editingId === businessUnit.id;
                return (
                  <Row key={businessUnit.id}>
                    <Td>
                      {isEditing ? (
                        <Input
                          autoFocus
                          value={editedName}
                          onChange={(event) => setEditedName(event.target.value)}
                          name={`business-unit-${businessUnit.id}`}
                        />
                      ) : businessUnit.name || ''}
                    </Td>
                    <Td>{businessUnit.usersCount || 0}</Td>
                    <Td>
                      {isEditing ? (
                        <Checkbox
                          checked={editedIsTest}
                          onChange={(event) => setEditedIsTest(event.target.checked)}
                          inputProps={{ 'aria-label': 'Test business unit' }}
                        />
                      ) : (businessUnit.isTest ? 'Yes' : 'No')}
                    </Td>
                    <Td>
                      <ActionsRow>
                        {isEditing ? (
                          <>
                            <SaveIconButton
                              type="button"
                              aria-label={t('Actions.Instance.save')}
                              title={t('Actions.Instance.save')}
                              onClick={onUpdateBusinessUnit}
                              disabled={currentBusy || !editedName.trim()}
                            >
                              {currentBusy ? <CircularProgress size={16} /> : <SaveIcon style={{ fontSize: 16 }} />}
                            </SaveIconButton>
                            <CancelIconButton
                              type="button"
                              aria-label={t('Actions.Instance.cancel')}
                              title={t('Actions.Instance.cancel')}
                              onClick={stopEditing}
                              disabled={currentBusy}
                            >
                              <CancelIcon style={{ fontSize: 20 }} />
                            </CancelIconButton>
                          </>
                        ) : (
                          <>
                            <EditIconButton
                              type="button"
                              aria-label={t('Actions.Instance.edit')}
                              title={t('Actions.Instance.edit')}
                              onClick={() => startEditing(businessUnit)}
                              disabled={Boolean(busyId) || Boolean(editingId)}
                            >
                              <EditIcon style={{ fontSize: 16 }} />
                            </EditIconButton>
                            <DeleteIconButton
                              type="button"
                              aria-label={t('Actions.Instance.delete')}
                              title={t('Actions.Instance.delete')}
                              onClick={() => confirmDeleteBusinessUnit(businessUnit)}
                              disabled={Boolean(busyId)}
                            >
                              {currentBusy ? <CircularProgress size={16} /> : <DeleteIcon style={{ fontSize: 16 }} />}
                            </DeleteIconButton>
                          </>
                        )}
                      </ActionsRow>
                    </Td>
                  </Row>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </Container>
    </PageShell>
  );
};

export default BusinessUnits;
